import { useMemo, useRef, useState, type ReactNode } from "react";
import { Check, Copy, Pencil, Plus, Trash2, X } from "lucide-react";
import { v4 } from "uuid";
import t from "@/lang/lang";
import { toast } from "sonner";
import {
  Address,
  addressListLabel,
  newAddress,
  oneLine,
} from "@/services/backend/addresses/types/address";
import { AddressFields } from "@/services/backend/addresses/components/address-card";
import { useBuildingsByAddressListQuery } from "@/services/backend/buildings/api/building-by-address-api";
import { useBuildingsShowQuery } from "@/services/backend/buildings/api/building-api";
import {
  Building,
  BuildingWithoutAreas,
} from "@/services/backend/buildings/types/building";
import {
  GfsAreaSnapshot,
  GfsBuildingSnapshot,
  GfsLevelSnapshot,
} from "@/services/backend/gfs/assessment/types";
import {
  GFS_AREA_TYPE_INSIDE,
  GFS_AREA_TYPE_OUTSIDE,
  GfsAreaType,
} from "@/services/backend/gfs/assessment/components/gfs-site-selector-components";
import {
  GfsOfferDocumentRow,
  GfsOfferDocumentSnapshot,
} from "@/services/backend/gfs/offer/types";
import { useGfsOfferEditorContext } from "@/services/backend/gfs/offer/components/offer-editor-context";
import { useGfsOfferUpdateObjectSelectionMutation } from "@/services/backend/gfs/offer/api";
import { GfsOfferHtmlEditor } from "@/services/backend/gfs/offer/components/offer-html-editor";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
} from "@/shared/components/ui/combobox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellInput,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";

type CustomerBuildingOption = BuildingWithoutAreas & {
  addressLabel: string;
};

type AvailableAddressOption = {
  id: string;
  address: Address;
  addressKey: string;
  createdAt: string;
  buildingCount: number;
};

type ObjectAddressGroup = {
  id: string;
  key: string;
  address: Address;
  buildings: GfsBuildingSnapshot[];
  createdAt?: string;
  isDraft?: boolean;
};

type ObjectAreaEntry = {
  building: GfsBuildingSnapshot;
  area: GfsAreaSnapshot;
};

type ObjectEntryDraft = {
  id: string;
  building?: GfsBuildingSnapshot;
  sourceBuildingId?: string;
  buildingInput: string;
  areaType: GfsAreaType;
  levelId: string;
  levelInput: string;
  areaId: string;
  areaInput: string;
};

type BuildingChoice =
  | {
      kind: "snapshot";
      building: GfsBuildingSnapshot;
      label: string;
    }
  | {
      kind: "customer";
      building: CustomerBuildingOption;
      label: string;
    };

type LevelChoice = {
  id: string;
  name: string;
  type: string;
};

type AreaChoice = GfsAreaSnapshot;

export function GfsOfferObjectPage() {
  const { offer, putRequest, updateOfferDocument } = useGfsOfferEditorContext();
  const document = putRequest.offerDocument ?? offer.offerDocument;

  return (
    <div className="space-y-4 pb-16">
      <ObjectSelectionAndSnapshot />
      <ObjectDocumentOverrides
        document={document}
        disabled={offer.immutable}
        onChange={updateOfferDocument}
      />
    </div>
  );
}

function ObjectSelectionAndSnapshot() {
  const { offer, putRequest, updateContent, replaceOffer } =
    useGfsOfferEditorContext();
  const { data, error } = useBuildingsByAddressListQuery({
    customer: { active: true, values: [offer.customerId] },
  });
  const [updateSelection, updateState] =
    useGfsOfferUpdateObjectSelectionMutation();
  const [newAddressDraft, setNewAddressDraft] = useState<Address>();
  const [hiddenAddressKeys, setHiddenAddressKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [hasObjectBuildingsOverride, setHasObjectBuildingsOverride] = useState(
    putRequest.content.objectBuildingsSet ??
      putRequest.content.objectBuildings.length > 0,
  );

  const buildings = useMemo(
    () =>
      (data?.items ?? []).flatMap((group) =>
        group.buildings.map((building) => ({
          ...building,
          addressLabel: oneLine(group.address) || t("Ohne Adresse"),
        })),
      ),
    [data?.items],
  );
  const availableAddresses = useMemo(() => {
    const options = new Map<string, AvailableAddressOption>();
    for (const building of buildings) {
      if (!hasAddress(building.address)) continue;
      const key = addressKey(building.address);
      const existing = options.get(key);
      if (existing) {
        existing.buildingCount += 1;
        existing.createdAt = earlierTimestamp(
          existing.createdAt,
          building.createdAt,
        );
        continue;
      }
      options.set(key, {
        id: `address-${building.id}`,
        address: { ...building.address },
        addressKey: key,
        createdAt: building.createdAt,
        buildingCount: 1,
      });
    }
    return [...options.values()].sort(compareAvailableAddresses);
  }, [buildings]);
  const objectBuildings = hasObjectBuildingsOverride
    ? putRequest.content.objectBuildings
    : offer.calculation.assessment.buildings;
  const selected = useMemo(
    () =>
      new Map(
        putRequest.content.objectBuildings
          .filter((building) => building.sourceId)
          .map((building) => [
            building.sourceId!,
            (building.areas ?? []).flatMap((area) =>
              area.sourceId ? [area.sourceId] : [],
            ),
          ]),
      ),
    [putRequest.content.objectBuildings],
  );
  const addressGroups = groupObjectBuildings(
    objectBuildings,
    putRequest.content.serviceAddress,
  );
  const draftAddressKey =
    newAddressDraft && hasAddress(newAddressDraft)
      ? addressKey(newAddressDraft)
      : undefined;
  const visibleAddressGroups = newAddressDraft
    ? [
        ...addressGroups.filter((group) => group.key !== draftAddressKey),
        {
          id: "draft-address",
          key: draftAddressKey ?? "draft-address",
          address: newAddressDraft,
          buildings: [],
          isDraft: true,
        },
      ]
    : addressGroups.filter((group) => !hiddenAddressKeys.has(group.key));

  const updateObjectBuildings = (nextBuildings: GfsBuildingSnapshot[]) => {
    setHasObjectBuildingsOverride(true);
    updateContent({
      objectBuildings: nextBuildings,
      objectBuildingsSet: true,
      ...(nextBuildings.length === 0 ? { serviceAddress: newAddress() } : {}),
    });
  };

  const submitSelection = (
    next: Map<string, string[]>,
    refreshSelected = false,
    localBuildingsOverride?: GfsBuildingSnapshot[],
  ) => {
    setHasObjectBuildingsOverride(true);
    const localBuildings =
      localBuildingsOverride ??
      objectBuildings.filter((building) => !building.sourceId);
    updateSelection({
      offerId: offer.id,
      refreshSelected,
      selections: [...next.entries()].map(
        ([sourceBuildingId, sourceAreaIds]) => ({
          sourceBuildingId,
          sourceAreaIds,
        }),
      ),
    })
      .unwrap()
      .then((nextOffer) => {
        if (localBuildings.length === 0) {
          replaceOffer(nextOffer);
          if (nextOffer.content.objectBuildings.length === 0) {
            updateContent({
              serviceAddress: newAddress(),
              objectBuildingsSet: true,
            });
          }
          return;
        }

        updateContent({
          objectBuildings: [
            ...nextOffer.content.objectBuildings,
            ...localBuildings,
          ],
          objectBuildingsSet: true,
        });
      });
  };

  const updateAddressGroup = (group: ObjectAddressGroup, address: Address) => {
    if (group.isDraft) {
      setNewAddressDraft(address);
      return;
    }

    const nextBuildings = objectBuildings.map((building) =>
      addressKey(
        hasAddress(building.address)
          ? building.address
          : putRequest.content.serviceAddress,
      ) === group.key
        ? { ...building, address, addressKey: addressKey(address) }
        : building,
    );
    updateContent({
      objectBuildings: nextBuildings,
      ...(addressGroups[0]?.key === group.key
        ? { serviceAddress: address }
        : {}),
    });
  };

  const removeBuilding = (building: GfsBuildingSnapshot) => {
    if (!building.sourceId) {
      updateObjectBuildings(
        objectBuildings.filter((candidate) => candidate.id !== building.id),
      );
      return;
    }

    const next = new Map(selected);
    next.delete(building.sourceId);
    submitSelection(next);
  };

  const removeArea = (building: GfsBuildingSnapshot, area: GfsAreaSnapshot) => {
    if (building.sourceId && area.sourceId) {
      const next = new Map(selected);
      const nextAreaIds = (next.get(building.sourceId) ?? []).filter(
        (areaId) => areaId !== area.sourceId,
      );
      if (nextAreaIds.length === 0) {
        next.delete(building.sourceId);
      } else {
        next.set(building.sourceId, nextAreaIds);
      }
      submitSelection(next);
      return;
    }

    updateObjectBuildings(
      objectBuildings.flatMap((candidate) => {
        if (candidate.id !== building.id) return [candidate];
        const areas = (candidate.areas ?? []).filter(
          (candidateArea) => candidateArea.id !== area.id,
        );
        return areas.length > 0 ? [{ ...candidate, areas }] : [];
      }),
    );
  };

  const removeAddressGroup = (group: ObjectAddressGroup) => {
    if (group.isDraft) {
      setNewAddressDraft(undefined);
      return;
    }

    setHiddenAddressKeys((current) => {
      const next = new Set(current);
      next.add(group.key);
      return next;
    });

    if (group.buildings.length === 0) {
      updateContent({
        objectBuildings: [],
        objectBuildingsSet: true,
        serviceAddress: newAddress(),
      });
      return;
    }

    const groupBuildingIds = new Set(
      group.buildings.map((building) => building.id),
    );
    const retainedLocalBuildings = objectBuildings.filter(
      (building) => !building.sourceId && !groupBuildingIds.has(building.id),
    );
    const next = new Map(selected);
    group.buildings.forEach((building) => {
      if (building.sourceId) next.delete(building.sourceId);
    });

    if (next.size === selected.size) {
      updateObjectBuildings(retainedLocalBuildings);
      return;
    }
    submitSelection(next, false, retainedLocalBuildings);
  };

  const addObjectArea = ({ building, area }: ObjectAreaEntry) => {
    if (hasDuplicateObjectArea(objectBuildings, building, area)) {
      toast.error(t("Dieser Bereich ist bereits ausgewählt."));
      return false;
    }

    const existingBuilding = objectBuildings.find((candidate) =>
      sameObjectBuilding(candidate, building),
    );
    const nextBuilding = existingBuilding
      ? mergeObjectBuildingEntry(existingBuilding, building, area)
      : {
          ...building,
          areas: (building.areas ?? []).some(
            (candidateArea) =>
              candidateArea.id === area.id ||
              objectAreaKey(building, candidateArea) ===
                objectAreaKey(building, area),
          )
            ? building.areas
            : [...(building.areas ?? []), area],
        };

    if (building.sourceId && area.sourceId) {
      const next = new Map(selected);
      const selectedAreaIds = next.get(building.sourceId) ?? [];
      if (!selectedAreaIds.includes(area.sourceId)) {
        next.set(building.sourceId, [...selectedAreaIds, area.sourceId]);
      }
      submitSelection(next);
      return true;
    }

    updateObjectBuildings(
      existingBuilding
        ? objectBuildings.map((candidate) =>
            candidate.id === existingBuilding.id ? nextBuilding : candidate,
          )
        : [...objectBuildings, nextBuilding],
    );
    return true;
  };

  const addObjectBuilding = (building: GfsBuildingSnapshot) => {
    const existingBuilding = objectBuildings.find((candidate) =>
      sameObjectBuildingNameAndAddress(
        candidate,
        building.name,
        building.address,
      ),
    );
    if (existingBuilding) {
      updateObjectBuildings(
        objectBuildings.map((candidate) =>
          candidate.id === existingBuilding.id
            ? mergeObjectBuildingSnapshots(candidate, building)
            : candidate,
        ),
      );
      return true;
    }
    updateObjectBuildings([...objectBuildings, { ...building, areas: [] }]);
    return true;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>{t("Objekt")}</CardTitle>
        </div>
        {selected.size > 0 && (
          <Button
            type="button"
            variant="outline"
            disabled={offer.immutable || updateState.isLoading}
            onClick={() => submitSelection(selected, true)}
          >
            {t("Kundendaten aktualisieren")}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="text-destructive text-sm">
            {t("Gebäude konnten nicht geladen werden.")}
          </div>
        )}

        <div className="space-y-3">
          {visibleAddressGroups.map((group, index) => (
            <ObjectAddressGroup
              key={group.id}
              group={group}
              reference={
                visibleAddressGroups.length > 1 ? `[${index + 1}]` : undefined
              }
              availableAddresses={availableAddresses}
              customerBuildings={buildings.filter(
                (building) =>
                  !hiddenAddressKeys.has(
                    addressKey(
                      hasAddress(building.address)
                        ? building.address
                        : putRequest.content.serviceAddress,
                    ),
                  ) &&
                  addressKey(
                    hasAddress(building.address)
                      ? building.address
                      : putRequest.content.serviceAddress,
                  ) === group.key,
              )}
              disabled={offer.immutable || updateState.isLoading}
              onAddressChange={updateAddressGroup}
              onAddressDelete={removeAddressGroup}
              onBuildingChange={(nextBuilding) =>
                updateObjectBuildings(
                  objectBuildings.map((building) =>
                    building.id === nextBuilding.id ? nextBuilding : building,
                  ),
                )
              }
              onBuildingDelete={removeBuilding}
              onAreaDelete={removeArea}
              onBuildingAdd={addObjectBuilding}
              onAddEntry={addObjectArea}
            />
          ))}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="justify-start px-0"
            disabled={
              offer.immutable || updateState.isLoading || !!newAddressDraft
            }
            onClick={() => setNewAddressDraft({ ...newAddress() })}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("Adresse hinzufügen")}
          </Button>
        </div>

        {objectBuildings.length === 0 && (
          <p className="text-muted-foreground text-sm">
            {t("Noch kein Gebäude hinterlegt.")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function ObjectAddressGroup({
  group,
  reference,
  availableAddresses,
  customerBuildings,
  disabled,
  onBuildingChange,
  onBuildingDelete,
  onAreaDelete,
  onBuildingAdd,
  onAddEntry,
  onAddressChange,
  onAddressDelete,
}: {
  group: ObjectAddressGroup;
  reference?: string;
  availableAddresses: AvailableAddressOption[];
  customerBuildings: CustomerBuildingOption[];
  disabled: boolean;
  onBuildingChange: (building: GfsBuildingSnapshot) => void;
  onBuildingDelete: (building: GfsBuildingSnapshot) => void;
  onAreaDelete: (building: GfsBuildingSnapshot, area: GfsAreaSnapshot) => void;
  onBuildingAdd: (building: GfsBuildingSnapshot) => boolean;
  onAddEntry: (entry: ObjectAreaEntry) => boolean;
  onAddressChange: (group: ObjectAddressGroup, address: Address) => void;
  onAddressDelete: (group: ObjectAddressGroup) => void;
}) {
  return (
    <section className="space-y-3 border-t pt-4 first:border-t-0 first:pt-0">
      {group.isDraft && availableAddresses.length > 0 && (
        <ObjectAddressSelector
          address={group.address}
          addresses={availableAddresses}
          disabled={disabled}
          onSelect={(address) => onAddressChange(group, address)}
        />
      )}
      <ObjectAddressTable
        group={group}
        reference={reference}
        disabled={disabled}
        onChange={(address) => onAddressChange(group, address)}
        onDelete={() => onAddressDelete(group)}
      />
      <ObjectBuildingsTable
        group={group}
        reference={reference}
        customerBuildings={customerBuildings}
        buildings={group.buildings}
        disabled={disabled}
        onBuildingChange={onBuildingChange}
        onBuildingDelete={onBuildingDelete}
        onAreaDelete={onAreaDelete}
        onBuildingAdd={onBuildingAdd}
        onAddEntry={onAddEntry}
      />
    </section>
  );
}

function ObjectAddressTable({
  group,
  reference,
  disabled,
  onChange,
  onDelete,
}: {
  group: ObjectAddressGroup;
  reference?: string;
  disabled: boolean;
  onChange: (address: Address) => void;
  onDelete: () => void;
}) {
  return (
    <ETable className="min-w-full table-fixed">
      <colgroup>
        <col style={{ width: "16%" }} />
        <col style={{ width: "14%" }} />
        <col style={{ width: "23%" }} />
        <col style={{ width: "9%" }} />
        <col style={{ width: "18%" }} />
        <col style={{ width: "13%" }} />
        <col style={{ width: "6.5rem" }} />
      </colgroup>
      <ETableHeader>
        <ETableRow>
          <ETableHead colSpan={7} className="text-foreground font-semibold">
            {t("Adresse") + (reference ? " " + reference : "")}
          </ETableHead>
        </ETableRow>
        <ETableRow>
          <ETableHead className="w-[16%]">{t("Firma")}</ETableHead>
          <ETableHead className="w-[14%]">{t("Abteilung")}</ETableHead>
          <ETableHead className="w-[23%]">{t("Straße und HsNr.")}</ETableHead>
          <ETableHead className="w-[9%]">{t("PLZ")}</ETableHead>
          <ETableHead className="w-[18%]">{t("Ort")}</ETableHead>
          <ETableHead className="w-[13%]">{t("Land")}</ETableHead>
          <ETableHead className="w-[6.5rem] text-center">
            {t("Aktion")}
          </ETableHead>
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        <AddressTableRow
          address={group.address}
          disabled={disabled}
          onChange={onChange}
          onDelete={onDelete}
        />
      </ETableBody>
    </ETable>
  );
}

function ObjectAddressSelector({
  address,
  addresses,
  disabled,
  onSelect,
}: {
  address: Address;
  addresses: AvailableAddressOption[];
  disabled: boolean;
  onSelect: (address: Address) => void;
}) {
  const selectedAddress = addresses.find(
    (option) => option.addressKey === addressKey(address),
  );

  return (
    <Combobox<AvailableAddressOption>
      value={selectedAddress ?? null}
      disabled={disabled}
      onValueChange={(option) => {
        onSelect(option ? { ...option.address } : newAddress());
      }}
      itemToStringLabel={(option) => addressListLabel(option.address)}
      itemToStringValue={(option) => option.addressKey}
    >
      <ComboboxInput
        aria-label={t("Verfügbare Adresse")}
        className="h-10 w-full max-w-[26rem]"
        placeholder={t("Adresse auswählen...")}
        showClear
      />
      <ComboboxContent className="w-[26rem]">
        <ComboboxEmpty>{t("Keine Adresse gefunden.")}</ComboboxEmpty>
        <ComboboxList>
          <ComboboxGroup>
            <ComboboxLabel>{t("Verfügbare Adressen")}</ComboboxLabel>
            {addresses.map((option) => (
              <ComboboxItem value={option} key={option.id}>
                <div className="min-w-0">
                  <div className="break-words">
                    {addressListLabel(option.address)}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {option.buildingCount} {t("Gebäude")}
                  </div>
                </div>
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

function AddressTableRow({
  address,
  disabled,
  onChange,
  onDelete,
}: {
  address: Address;
  disabled: boolean;
  onChange: (address: Address) => void;
  onDelete: () => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(address);

  return (
    <ETableRow>
      <ETableCellInput
        value={address.company}
        disabled={disabled}
        placeholder={t("Firma")}
        aria-label={t("Firma")}
        className="w-full min-w-0"
        onChange={(event) =>
          onChange({ ...address, company: event.target.value })
        }
      />
      <ETableCellInput
        value={address.department}
        disabled={disabled}
        placeholder={t("Abteilung")}
        aria-label={t("Abteilung")}
        className="w-full min-w-0"
        onChange={(event) =>
          onChange({ ...address, department: event.target.value })
        }
      />
      <ETableCellInput
        value={address.street}
        disabled={disabled}
        placeholder={t("Straße und HsNr.")}
        aria-label={t("Straße und HsNr.")}
        className="w-full min-w-0"
        onChange={(event) =>
          onChange({ ...address, street: event.target.value })
        }
      />
      <ETableCellInput
        value={address.postalCode}
        disabled={disabled}
        placeholder={t("PLZ")}
        aria-label={t("PLZ")}
        className="w-full min-w-0"
        onChange={(event) =>
          onChange({ ...address, postalCode: event.target.value })
        }
      />
      <ETableCellInput
        value={address.city}
        disabled={disabled}
        placeholder={t("Ort")}
        aria-label={t("Ort")}
        className="w-full min-w-0"
        onChange={(event) => onChange({ ...address, city: event.target.value })}
      />
      <ETableCellInput
        value={address.country}
        disabled={disabled}
        placeholder={t("Land")}
        aria-label={t("Land")}
        className="w-full min-w-0"
        onChange={(event) =>
          onChange({ ...address, country: event.target.value })
        }
      />
      <ETableCell className="w-[6.5rem] p-0 text-center">
        <div className="flex justify-center gap-1">
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              if (open) setDraft({ ...address });
              setDialogOpen(open);
            }}
          >
            <DialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={disabled}
                aria-label={t("Adresse bearbeiten")}
                title={t("Adresse bearbeiten")}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t("Adresse bearbeiten")}</DialogTitle>
                <DialogDescription>
                  {t("Vollständige Adresse bearbeiten")}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <AddressFields
                  address={draft}
                  onAddressChange={setDraft}
                  disabled={disabled}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid items-center gap-1.5">
                    <Label htmlFor="address-postbox">{t("Postfach")}</Label>
                    <Input
                      id="address-postbox"
                      value={draft.postbox}
                      placeholder={t("Postfach")}
                      disabled={disabled}
                      onChange={(event) =>
                        setDraft({ ...draft, postbox: event.target.value })
                      }
                    />
                  </div>
                  <div className="grid items-center gap-1.5">
                    <Label htmlFor="address-comment">{t("Kommentar")}</Label>
                    <Textarea
                      id="address-comment"
                      value={draft.comment}
                      placeholder={t("Kommentar")}
                      disabled={disabled}
                      onChange={(event) =>
                        setDraft({ ...draft, comment: event.target.value })
                      }
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  {t("Abbrechen")}
                </Button>
                <Button
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    onChange(draft);
                    setDialogOpen(false);
                  }}
                >
                  {t("Speichern")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={t("Adresse aus Auswahl entfernen")}
            title={t("Adresse aus Auswahl entfernen")}
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </ETableCell>
    </ETableRow>
  );
}

function ObjectBuildingsTable({
  group,
  reference,
  customerBuildings,
  buildings,
  disabled,
  onBuildingChange,
  onBuildingDelete,
  onAreaDelete,
  onBuildingAdd,
  onAddEntry,
}: {
  group: ObjectAddressGroup;
  reference?: string;
  customerBuildings: CustomerBuildingOption[];
  buildings: GfsBuildingSnapshot[];
  disabled: boolean;
  onBuildingChange: (building: GfsBuildingSnapshot) => void;
  onBuildingDelete: (building: GfsBuildingSnapshot) => void;
  onAreaDelete: (building: GfsBuildingSnapshot, area: GfsAreaSnapshot) => void;
  onBuildingAdd: (building: GfsBuildingSnapshot) => boolean;
  onAddEntry: (entry: ObjectAreaEntry) => boolean;
}) {
  const [draftRows, setDraftRows] = useState<ObjectEntryDraft[]>([]);
  const visibleBuildings = buildings;
  const copySource = [...buildings]
    .reverse()
    .find((building) => (building.areas ?? []).length > 0);
  const copyArea = copySource?.areas?.[copySource.areas.length - 1];

  const createDraft = (sourceBuilding?: GfsBuildingSnapshot) => {
    const sourceArea = sourceBuilding?.areas?.[sourceBuilding.areas.length - 1];
    const sourceLevel = sourceArea?.levelId
      ? sourceBuilding?.levels?.find((level) => level.id === sourceArea.levelId)
      : undefined;
    return {
      id: v4(),
      building: sourceBuilding,
      sourceBuildingId: sourceBuilding?.sourceId,
      buildingInput: sourceBuilding?.name ?? "",
      areaType: toOfferAreaType(sourceArea?.type),
      levelId: sourceArea?.levelId ?? "",
      levelInput: sourceLevel?.name ?? "",
      areaId: "",
      areaInput: "",
    } satisfies ObjectEntryDraft;
  };

  const updateDraft = (id: string, patch: Partial<ObjectEntryDraft>) => {
    setDraftRows((current) =>
      current.map((draft) =>
        draft.id === id ? { ...draft, ...patch } : draft,
      ),
    );
  };

  const commitDraft = (id: string, entry: ObjectAreaEntry) => {
    if (!onAddEntry(entry)) return false;
    setDraftRows((current) => current.filter((draft) => draft.id !== id));
    return true;
  };

  return (
    <ETable className="w-full table-fixed">
      <colgroup>
        <col style={{ width: "27%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "19%" }} />
        <col style={{ width: "32%" }} />
        <col style={{ width: "3.5rem" }} />
      </colgroup>
      <ETableHeader>
        <ETableRow>
          <ETableHead colSpan={5} className="text-foreground font-semibold">
            {t("Gebäudedaten") + (reference ? " " + reference : "")}
          </ETableHead>
        </ETableRow>
        <ETableRow>
          <ETableHead className="w-[27%]">{t("Gebäude")}</ETableHead>
          <ETableHead className="w-[15%]">{t("Bereichsart")}</ETableHead>
          <ETableHead className="w-[19%]">{t("Ebene")}</ETableHead>
          <ETableHead className="w-[32%]">{t("Bereich")}</ETableHead>
          <ETableHead className="w-[7%] text-center">{t("Aktion")}</ETableHead>
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        {visibleBuildings.flatMap((building) => {
          const areas = building.areas ?? [];
          return areas.length > 0
            ? areas.map((area) => (
                <ObjectBuildingAreaRow
                  key={`${building.id}-${area.id}`}
                  building={building}
                  area={area}
                  disabled={disabled}
                  onBuildingChange={onBuildingChange}
                  onAreaDelete={onAreaDelete}
                />
              ))
            : [
                <ObjectBuildingAreaRow
                  key={`${building.id}-empty`}
                  building={building}
                  area={undefined}
                  disabled={disabled}
                  onBuildingChange={onBuildingChange}
                  onBuildingDelete={onBuildingDelete}
                  onAreaDelete={onAreaDelete}
                />,
              ];
        })}
        {draftRows.map((draft) => (
          <ObjectEntryDraftRow
            key={draft.id}
            group={group}
            customerBuildings={customerBuildings}
            draft={draft}
            disabled={disabled}
            onChange={(patch) => updateDraft(draft.id, patch)}
            onBuildingCommit={(building) => {
              const committed = onBuildingAdd(building);
              if (!committed) return false;
              updateDraft(draft.id, {
                building,
                sourceBuildingId: building.sourceId,
                buildingInput: building.name,
              });
              return true;
            }}
            onCommit={(entry) => commitDraft(draft.id, entry)}
            onSaved={() =>
              setDraftRows((current) =>
                current.filter((candidate) => candidate.id !== draft.id),
              )
            }
            onCancel={() =>
              setDraftRows((current) =>
                current.filter((candidate) => candidate.id !== draft.id),
              )
            }
          />
        ))}
        <ETableRow>
          <ETableCell colSpan={5} className="p-1">
            <div className="flex justify-end gap-1">
              {copySource && copyArea && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={disabled}
                  aria-label={t("Gebäude und Ebene kopieren")}
                  title={t("Gebäude und Ebene kopieren")}
                  onClick={() =>
                    setDraftRows((current) => [
                      ...current,
                      createDraft(copySource),
                    ])
                  }
                >
                  <Copy className="mr-2 h-4 w-4" />
                  {t("Gebäude und Ebene kopieren")}
                </Button>
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() =>
                  setDraftRows((current) => [...current, createDraft()])
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("Eintrag hinzufügen")}
              </Button>
            </div>
          </ETableCell>
        </ETableRow>
      </ETableBody>
    </ETable>
  );
}

function ObjectEntryDraftRow({
  group,
  customerBuildings,
  draft,
  disabled,
  onChange,
  onBuildingCommit,
  onCommit,
  onSaved,
  onCancel,
}: {
  group: ObjectAddressGroup;
  customerBuildings: CustomerBuildingOption[];
  draft: ObjectEntryDraft;
  disabled: boolean;
  onChange: (patch: Partial<ObjectEntryDraft>) => void;
  onBuildingCommit: (building: GfsBuildingSnapshot) => boolean;
  onCommit: (entry: ObjectAreaEntry) => boolean;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const shouldLoadSource = !!draft.sourceBuildingId;
  const { data: loadedBuilding, isLoading: isLoadingBuilding } =
    useBuildingsShowQuery(
      { id: draft.sourceBuildingId ?? "" },
      { skip: !shouldLoadSource },
    );
  const loadedSnapshot = loadedBuilding
    ? toGfsBuildingSnapshot(loadedBuilding)
    : undefined;
  const selectedBuilding = loadedSnapshot
    ? {
        ...loadedSnapshot,
        address: { ...group.address },
        addressKey: addressKey(group.address),
        levels: mergeDraftLevels(loadedSnapshot.levels, draft.building?.levels),
        areas: mergeDraftAreas(loadedSnapshot.areas, draft.building?.areas),
      }
    : draft.building;
  const buildingChoices = objectBuildingChoices(
    group.buildings,
    customerBuildings,
  );
  const selectedBuildingChoice = selectedBuilding
    ? (buildingChoices.find(
        (choice) =>
          choice.building.id === selectedBuilding.id ||
          (choice.kind === "customer" &&
            choice.building.id === selectedBuilding.sourceId),
      ) ?? {
        kind: "snapshot" as const,
        building: selectedBuilding,
        label: selectedBuilding.name,
      })
    : null;
  const levels = (selectedBuilding?.levels ?? []).filter(
    (level) => !level.type || level.type === draft.areaType,
  );
  const levelChoices: LevelChoice[] = [
    { id: "", name: t("Ohne Ebene"), type: draft.areaType },
    ...levels,
  ];
  const selectedLevel =
    levelChoices.find((level) => level.id === draft.levelId) ?? null;
  const areas = (selectedBuilding?.areas ?? []).filter(
    (area) =>
      (area.levelId ?? "") === draft.levelId &&
      (!area.type || area.type === draft.areaType),
  );
  const selectedArea = areas.find((area) => area.id === draft.areaId) ?? null;
  const isCommittingRef = useRef(false);

  const findExistingBuildingChoice = (value: string): BuildingChoice | null => {
    const name = value.trim();
    const existingSnapshot = group.buildings.find((building) =>
      sameObjectBuildingNameAndAddress(building, name, group.address),
    );
    if (existingSnapshot) {
      return {
        kind: "snapshot",
        building: existingSnapshot,
        label: existingSnapshot.name,
      };
    }

    const existingCustomerBuilding = customerBuildings.find(
      (building) =>
        normalizeObjectName(building.name) === normalizeObjectName(name) &&
        addressKey(building.address) === addressKey(group.address),
    );
    if (existingCustomerBuilding) {
      return {
        kind: "customer",
        building: existingCustomerBuilding,
        label: existingCustomerBuilding.name,
      };
    }
    return null;
  };

  const createNewBuildingSnapshot = (name: string): GfsBuildingSnapshot => ({
    id: v4(),
    name,
    address: { ...group.address },
    addressKey: addressKey(group.address),
    levels: [],
    areas: [],
    takenAt: new Date().toISOString(),
  });

  const updateBuilding = (choice: BuildingChoice) => {
    const snapshot =
      choice.kind === "snapshot"
        ? { ...choice.building }
        : customerBuildingSnapshot(choice.building, group.address);
    onChange({
      building: snapshot,
      sourceBuildingId: snapshot.sourceId,
      buildingInput: snapshot.name,
      levelId: "",
      levelInput: "",
      areaId: "",
      areaInput: "",
    });
  };

  const createBuilding = (value: string) => {
    const name = value.trim();
    if (!name) return;
    if (
      draft.building &&
      normalizeObjectName(draft.building.name) === normalizeObjectName(name)
    ) {
      return;
    }
    const existingChoice = findExistingBuildingChoice(name);
    if (existingChoice) {
      updateBuilding(existingChoice);
      return;
    }
    const building = createNewBuildingSnapshot(name);
    updateBuilding({
      kind: "snapshot",
      building,
      label: name,
    });
  };

  const materializeTypedBuilding = () => {
    const name = draft.buildingInput.trim();
    if (!name || isLoadingBuilding) return;
    const existingChoice = findExistingBuildingChoice(name);
    if (existingChoice) {
      updateBuilding(existingChoice);
      return;
    }
    if (
      draft.building &&
      normalizeObjectName(draft.building.name) === normalizeObjectName(name)
    ) {
      return;
    }
    createBuilding(name);
  };

  const updateAreaType = (areaType: GfsAreaType) => {
    onChange({
      areaType,
      levelId: "",
      levelInput: "",
      areaId: "",
      areaInput: "",
    });
  };

  const selectLevel = (choice: LevelChoice) => {
    onChange({ levelId: choice.id, levelInput: "", areaId: "", areaInput: "" });
  };

  const createLevel = (value: string) => {
    if (!selectedBuilding) return;
    const name = value.trim();
    if (!name) return;
    const existingLevel = levels.find(
      (level) => normalizeObjectName(level.name) === normalizeObjectName(name),
    );
    if (existingLevel) {
      selectLevel(existingLevel);
      return;
    }
    const level: GfsLevelSnapshot = {
      id: v4(),
      name,
      type: draft.areaType,
    };
    onChange({
      building: {
        ...selectedBuilding,
        levels: [...(selectedBuilding.levels ?? []), level],
      },
      levelId: level.id,
      levelInput: "",
      areaId: "",
      areaInput: "",
    });
  };

  const commitTypedLevel = () => {
    if (draft.levelId || !draft.levelInput.trim()) return;
    createLevel(draft.levelInput);
  };

  const commitArea = (area: GfsAreaSnapshot, building = selectedBuilding) => {
    if (!building || !area.name.trim() || isCommittingRef.current) return;
    isCommittingRef.current = true;
    const committed = onCommit({
      building: {
        ...building,
        address: { ...group.address },
        addressKey: addressKey(group.address),
      },
      area,
    });
    if (!committed) isCommittingRef.current = false;
  };

  const selectArea = (area: AreaChoice) => {
    onChange({ areaId: area.id, areaInput: area.name });
  };

  const createArea = (value: string) => {
    const name = value.trim();
    if (!name) return;
    if (!selectedBuilding) return;
    const existingArea = areas.find(
      (area) => normalizeObjectName(area.name) === normalizeObjectName(name),
    );
    if (existingArea) {
      selectArea(existingArea);
      return;
    }
    onChange({ areaId: "", areaInput: name });
  };

  const saveDraft = () => {
    const name = draft.buildingInput.trim();
    if (!name || isLoadingBuilding || isCommittingRef.current) return;

    const existingChoice = findExistingBuildingChoice(name);
    const selectedBuildingMatches =
      selectedBuilding &&
      normalizeObjectName(selectedBuilding.name) === normalizeObjectName(name);
    const baseBuilding = selectedBuildingMatches
      ? selectedBuilding
      : existingChoice
        ? existingChoice.kind === "snapshot"
          ? existingChoice.building
          : customerBuildingSnapshot(existingChoice.building, group.address)
        : createNewBuildingSnapshot(name);
    const nextBuilding: GfsBuildingSnapshot = {
      ...baseBuilding,
      name,
      address: { ...group.address },
      addressKey: addressKey(group.address),
      levels: [...(baseBuilding.levels ?? [])],
      areas: [...(baseBuilding.areas ?? [])],
    };
    let levelId = draft.levelId;
    if (!levelId && draft.levelInput.trim()) {
      const existingLevel = nextBuilding.levels.find(
        (level) =>
          level.type === draft.areaType &&
          normalizeObjectName(level.name) ===
            normalizeObjectName(draft.levelInput),
      );
      if (existingLevel) {
        levelId = existingLevel.id;
      } else {
        const level = {
          id: v4(),
          name: draft.levelInput.trim(),
          type: draft.areaType,
        } satisfies GfsLevelSnapshot;
        nextBuilding.levels.push(level);
        levelId = level.id;
      }
    }

    const selectedPersistedArea = draft.areaId
      ? nextBuilding.areas.find((area) => area.id === draft.areaId)
      : undefined;
    const typedAreaName = draft.areaInput.trim();
    const existingArea = typedAreaName
      ? nextBuilding.areas.find(
          (area) =>
            normalizeObjectName(area.name) ===
              normalizeObjectName(typedAreaName) &&
            (area.levelId ?? "") === levelId &&
            (!area.type || area.type === draft.areaType),
        )
      : undefined;
    const area = selectedPersistedArea ?? existingArea;

    if (area) {
      if (hasDuplicateObjectArea(group.buildings, nextBuilding, area)) {
        onSaved();
        return;
      }
      commitArea(area, nextBuilding);
      return;
    }

    if (typedAreaName) {
      const nextArea: GfsAreaSnapshot = {
        id: v4(),
        levelId: levelId || undefined,
        name: typedAreaName,
        type: draft.areaType,
      };
      nextBuilding.areas.push(nextArea);
      commitArea(nextArea, nextBuilding);
      return;
    }

    if (onBuildingCommit(nextBuilding)) onSaved();
  };

  return (
    <ETableRow className="bg-muted/20 align-top">
      <ETableCell className="p-0">
        <ObjectEntryCombobox
          value={selectedBuildingChoice}
          inputValue={draft.buildingInput}
          options={buildingChoices}
          disabled={disabled || isLoadingBuilding}
          placeholder={t("Gebäude auswählen oder anlegen...")}
          ariaLabel={t("Gebäude")}
          emptyLabel={t("Gebäude nicht vorhanden.")}
          onInputValueChange={(buildingInput) => {
            const selectedBuildingName = draft.building?.name ?? "";
            const isSearchingFromSelectedBuilding =
              !!draft.building &&
              normalizeObjectName(buildingInput) !==
                normalizeObjectName(selectedBuildingName);
            onChange({
              buildingInput,
              ...(isSearchingFromSelectedBuilding
                ? {
                    building: undefined,
                    sourceBuildingId: undefined,
                    levelId: "",
                    levelInput: "",
                    areaId: "",
                    areaInput: "",
                  }
                : {}),
            });
          }}
          onInputBlur={materializeTypedBuilding}
          onValueChange={updateBuilding}
          onCreate={createBuilding}
          optionLabel={(choice) => choice.label}
          optionValue={(choice) => choice.label}
          optionContent={(choice) => choice.label}
        />
      </ETableCell>
      <ETableCell className="p-0">
        <Select
          value={draft.areaType}
          disabled={disabled || !selectedBuilding}
          onValueChange={(value) => updateAreaType(value as GfsAreaType)}
        >
          <SelectTrigger className="h-10 w-full min-w-0 border-0 shadow-none focus:ring-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={GFS_AREA_TYPE_INSIDE}>
              {t("Innenbereich")}
            </SelectItem>
            <SelectItem value={GFS_AREA_TYPE_OUTSIDE}>
              {t("Außenbereich")}
            </SelectItem>
          </SelectContent>
        </Select>
      </ETableCell>
      <ETableCell className="p-0">
        <ObjectEntryCombobox
          value={selectedLevel}
          inputValue={draft.levelInput}
          options={levelChoices}
          disabled={disabled || !selectedBuilding || isLoadingBuilding}
          placeholder={t("Ebene auswählen oder anlegen...")}
          ariaLabel={t("Ebene")}
          emptyLabel={t("Ebene nicht vorhanden.")}
          onInputValueChange={(levelInput) =>
            onChange({ levelInput, levelId: "" })
          }
          onInputBlur={commitTypedLevel}
          onValueChange={selectLevel}
          onCreate={createLevel}
          optionLabel={(level) => level.name}
          optionValue={(level) => level.name}
          optionContent={(level) => (level.id ? level.name : `${level.name}`)}
        />
      </ETableCell>
      <ETableCell className="p-0">
        <ObjectEntryCombobox
          value={selectedArea}
          inputValue={draft.areaInput}
          options={areas}
          disabled={disabled || !selectedBuilding || isLoadingBuilding}
          placeholder={t("Bereich auswählen oder anlegen...")}
          ariaLabel={t("Bereich")}
          emptyLabel={t("Bereich nicht vorhanden.")}
          onInputValueChange={(areaInput) =>
            onChange({ areaInput, areaId: "" })
          }
          onValueChange={selectArea}
          onCreate={createArea}
          optionLabel={(area) => area.name}
          optionValue={(area) => area.name}
          optionContent={(area) => area.name}
        />
      </ETableCell>
      <ETableCell className="w-[7%] p-0 text-center">
        <div className="flex justify-center">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={
              disabled || !draft.buildingInput.trim() || isLoadingBuilding
            }
            aria-label={t("Speichern")}
            title={t("Speichern")}
            onClick={saveDraft}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={t("Abbrechen")}
            title={t("Abbrechen")}
            onClick={onCancel}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </ETableCell>
    </ETableRow>
  );
}

function ObjectEntryCombobox<T>({
  value,
  inputValue,
  options,
  disabled,
  placeholder,
  ariaLabel,
  emptyLabel,
  onInputValueChange,
  onInputBlur,
  onValueChange,
  onCreate,
  optionLabel,
  optionValue,
  optionContent,
}: {
  value: T | null;
  inputValue: string;
  options: T[];
  disabled: boolean;
  placeholder: string;
  ariaLabel: string;
  emptyLabel: string;
  onInputValueChange: (value: string) => void;
  onInputBlur?: () => void;
  onValueChange: (value: T) => void;
  onCreate: (value: string) => void;
  optionLabel: (value: T) => string;
  optionValue: (value: T) => string;
  optionContent: (value: T) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const handleCreate = () => {
    if (!inputValue.trim()) return;
    onCreate(inputValue);
    setOpen(false);
  };

  return (
    <Combobox<T>
      value={value}
      inputValue={inputValue}
      open={open}
      disabled={disabled}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) onInputBlur?.();
      }}
      onInputValueChange={(next, eventDetails) => {
        if (eventDetails.reason !== "input-change") return;
        onInputValueChange(next);
      }}
      onValueChange={(next, eventDetails) => {
        if (
          !next ||
          (eventDetails.reason !== "item-press" &&
            eventDetails.reason !== "input-press")
        ) {
          return;
        }
        setOpen(false);
        onValueChange(next);
      }}
      itemToStringLabel={optionLabel}
      itemToStringValue={optionValue}
    >
      <ComboboxInput
        aria-label={ariaLabel}
        className="h-10 w-full min-w-0 rounded-none border-0 shadow-none"
        placeholder={placeholder}
        onBlur={onInputBlur}
        onKeyDown={(event) => {
          if (event.key === "Enter" && inputValue.trim()) {
            event.preventDefault();
            event.stopPropagation();
            handleCreate();
          }
        }}
      />
      <ComboboxContent className="min-w-[18rem]">
        <ComboboxEmpty>
          <div className="flex flex-col gap-2 px-3 py-2 text-sm">
            <span>{emptyLabel}</span>
            <Button
              type="button"
              size="sm"
              disabled={!inputValue.trim()}
              onClick={handleCreate}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t("Neu hinzufügen")}
            </Button>
          </div>
        </ComboboxEmpty>
        <ComboboxList>
          <ComboboxGroup>
            {options.map((option) => (
              <ComboboxItem value={option} key={optionValue(option)}>
                {optionContent(option)}
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}

function ObjectBuildingAreaRow({
  building,
  area,
  disabled,
  onBuildingChange,
  onBuildingDelete,
  onAreaDelete,
}: {
  building: GfsBuildingSnapshot;
  area?: GfsAreaSnapshot;
  disabled: boolean;
  onBuildingChange: (building: GfsBuildingSnapshot) => void;
  onBuildingDelete?: (building: GfsBuildingSnapshot) => void;
  onAreaDelete: (building: GfsBuildingSnapshot, area: GfsAreaSnapshot) => void;
}) {
  const hasDescribedArea = Boolean(
    area?.name.trim() && area.name.trim() !== t("Bereich"),
  );
  const level = area?.levelId
    ? (building.levels ?? []).find((candidate) => candidate.id === area.levelId)
    : undefined;

  const updateArea = (patch: Partial<GfsAreaSnapshot>) => {
    const nextArea = area ?? {
      id: v4(),
      name: "",
      type: GFS_AREA_TYPE_INSIDE,
    };
    onBuildingChange({
      ...building,
      areas: area
        ? (building.areas ?? []).map((candidate) =>
            candidate.id === nextArea.id
              ? { ...candidate, ...patch }
              : candidate,
          )
        : [...(building.areas ?? []), { ...nextArea, ...patch }],
    });
  };

  const updateLevel = (name: string) => {
    const trimmedName = name.trim();
    const nextArea = area ?? {
      id: v4(),
      name: "",
      type: GFS_AREA_TYPE_INSIDE,
    };
    if (!trimmedName) {
      updateArea({ levelId: undefined });
      return;
    }

    const levelId = level?.id ?? v4();
    const levels = level
      ? (building.levels ?? []).map((candidate) =>
          candidate.id === level.id
            ? { ...candidate, name: trimmedName }
            : candidate,
        )
      : [
          ...(building.levels ?? []),
          { id: levelId, name: trimmedName, type: nextArea.type },
        ];
    onBuildingChange({
      ...building,
      levels,
      areas: area
        ? (building.areas ?? []).map((candidate) =>
            candidate.id === nextArea.id
              ? { ...candidate, levelId }
              : candidate,
          )
        : [...(building.areas ?? []), { ...nextArea, levelId }],
    });
  };

  return (
    <ETableRow>
      <ETableCellInput
        value={building.name}
        disabled={disabled}
        placeholder={t("Gebäude")}
        aria-label={t("Gebäude")}
        className="min-w-48"
        onChange={(event) =>
          onBuildingChange({ ...building, name: event.target.value })
        }
      />
      <ETableCell className="p-0">
        <Select
          value={area?.type ?? GFS_AREA_TYPE_INSIDE}
          disabled={disabled || hasDescribedArea}
          onValueChange={(type) =>
            updateArea({
              type,
              levelId: undefined,
              ...(hasDescribedArea ? {} : { name: "" }),
            })
          }
        >
          <SelectTrigger
            className="h-10 w-full min-w-0 border-0 shadow-none focus:ring-0"
            aria-label={t("Bereichsart")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="INSIDE">{t("Innenbereich")}</SelectItem>
            <SelectItem value="OUTSIDE">{t("Außenbereich")}</SelectItem>
          </SelectContent>
        </Select>
      </ETableCell>
      <ETableCellInput
        value={level?.name ?? ""}
        disabled={disabled || hasDescribedArea}
        placeholder={t("Ebene")}
        aria-label={t("Ebene")}
        className="min-w-40"
        onChange={(event) => updateLevel(event.target.value)}
      />
      <ETableCellInput
        value={area?.name ?? ""}
        disabled={disabled}
        placeholder={t("Bereich")}
        aria-label={t("Bereich")}
        className="min-w-56"
        onChange={(event) => updateArea({ name: event.target.value })}
      />
      <ETableCell className="w-[7%] p-0 text-center">
        <div className="flex justify-center">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label={
              hasDescribedArea
                ? t("Bereich aus Auswahl entfernen")
                : t("Gebäude aus Auswahl entfernen")
            }
            title={
              hasDescribedArea
                ? t("Bereich aus Auswahl entfernen")
                : t("Gebäude aus Auswahl entfernen")
            }
            onClick={() => {
              if (hasDescribedArea && area) {
                onAreaDelete(building, area);
              } else {
                onBuildingDelete?.(building);
              }
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </ETableCell>
    </ETableRow>
  );
}

function groupObjectBuildings(
  buildings: GfsBuildingSnapshot[],
  fallbackAddress: Address,
): ObjectAddressGroup[] {
  const groups = new Map<string, ObjectAddressGroup>();
  for (const building of buildings) {
    const address = hasAddress(building.address)
      ? building.address
      : fallbackAddress;
    const key = addressKey(address);
    const group = groups.get(key);
    if (group) {
      group.buildings.push(building);
      group.createdAt = earlierTimestamp(group.createdAt, building.takenAt);
      continue;
    }
    groups.set(key, {
      id: `object-${building.id}`,
      key,
      address,
      buildings: [building],
      createdAt: building.takenAt,
    });
  }
  if (groups.size === 0 && hasAddress(fallbackAddress)) {
    const key = addressKey(fallbackAddress);
    groups.set(key, {
      id: "fallback-address",
      key,
      address: fallbackAddress,
      buildings: [],
    });
  }
  return [...groups.values()].sort(compareAddressGroups);
}

function earlierTimestamp(left?: string, right?: string) {
  if (!left) return right ?? "";
  if (!right) return left;
  return left < right ? left : right;
}

function compareAddressGroups(
  left: ObjectAddressGroup,
  right: ObjectAddressGroup,
) {
  const createdAtOrder = (right.createdAt ?? "").localeCompare(
    left.createdAt ?? "",
  );
  if (createdAtOrder !== 0) return createdAtOrder;
  return left.id.localeCompare(right.id);
}

function compareAvailableAddresses(
  left: AvailableAddressOption,
  right: AvailableAddressOption,
) {
  const createdAtOrder = right.createdAt.localeCompare(left.createdAt);
  if (createdAtOrder !== 0) return createdAtOrder;
  return left.id.localeCompare(right.id);
}

function toGfsBuildingSnapshot(building: Building): GfsBuildingSnapshot {
  return {
    id: building.id,
    sourceId: building.id,
    name: building.name,
    address: { ...building.address },
    addressKey: building.addressKey,
    levels: building.levels.map((level) => ({
      id: level.id,
      sourceId: level.id,
      name: level.name,
      type: String(level.type),
    })),
    areas: building.areas.map((area) => ({
      id: area.id,
      sourceId: area.id,
      levelId: area.levelId ?? undefined,
      name: area.name,
      type: String(area.type),
    })),
    takenAt: new Date().toISOString(),
  };
}

function objectBuildingChoices(
  snapshots: GfsBuildingSnapshot[],
  customerBuildings: CustomerBuildingOption[],
): BuildingChoice[] {
  const importedSourceIds = new Set(
    snapshots.flatMap((building) =>
      building.sourceId ? [building.sourceId] : [],
    ),
  );
  const snapshotChoices = snapshots.map((building) => ({
    kind: "snapshot" as const,
    building,
    label: objectBuildingLabel(building),
  }));
  const customerChoices = customerBuildings
    .filter((building) => !importedSourceIds.has(building.id))
    .map((building) => ({
      kind: "customer" as const,
      building,
      label: objectBuildingLabel(building),
    }));
  return [...snapshotChoices, ...customerChoices];
}

function mergeDraftLevels(
  sourceLevels: GfsLevelSnapshot[],
  draftLevels?: GfsLevelSnapshot[],
) {
  const customLevels = (draftLevels ?? []).filter((level) => !level.sourceId);
  return [...sourceLevels, ...customLevels];
}

function mergeDraftAreas(
  sourceAreas: GfsAreaSnapshot[],
  draftAreas?: GfsAreaSnapshot[],
) {
  const customAreas = (draftAreas ?? []).filter((area) => !area.sourceId);
  return [...sourceAreas, ...customAreas];
}

function objectBuildingLabel(
  building: GfsBuildingSnapshot | CustomerBuildingOption,
) {
  return [building.name, oneLine(building.address) || t("Ohne Adresse")]
    .filter(Boolean)
    .join(" – ");
}

function customerBuildingSnapshot(
  building: CustomerBuildingOption,
  address: Address,
): GfsBuildingSnapshot {
  return {
    id: building.id,
    sourceId: building.id,
    name: building.name,
    address: { ...address },
    addressKey: addressKey(address),
    levels: [],
    areas: [],
    takenAt: building.createdAt,
  };
}

function toOfferAreaType(value?: string): GfsAreaType {
  return value === GFS_AREA_TYPE_OUTSIDE
    ? GFS_AREA_TYPE_OUTSIDE
    : GFS_AREA_TYPE_INSIDE;
}

function normalizeObjectName(value: string) {
  return value.trim().toLocaleLowerCase();
}

function sameObjectBuildingNameAndAddress(
  building: GfsBuildingSnapshot | CustomerBuildingOption,
  name: string,
  address: Address,
) {
  return (
    normalizeObjectName(building.name) === normalizeObjectName(name) &&
    addressKey(building.address) === addressKey(address)
  );
}

function sameObjectBuilding(
  left: GfsBuildingSnapshot,
  right: GfsBuildingSnapshot,
) {
  if (left.sourceId && right.sourceId) {
    return left.sourceId === right.sourceId;
  }
  return sameObjectBuildingNameAndAddress(right, left.name, left.address);
}

function objectAreaKey(building: GfsBuildingSnapshot, area: GfsAreaSnapshot) {
  if (area.sourceId) return `source:${area.sourceId}`;
  const levelName = area.levelId
    ? (building.levels.find((level) => level.id === area.levelId)?.name ?? "")
    : "";
  return [
    normalizeObjectName(area.name),
    toOfferAreaType(area.type),
    normalizeObjectName(levelName),
  ].join("\x1f");
}

function hasDuplicateObjectArea(
  buildings: GfsBuildingSnapshot[],
  building: GfsBuildingSnapshot,
  area: GfsAreaSnapshot,
) {
  const areaKey = objectAreaKey(building, area);
  return buildings.some(
    (candidate) =>
      sameObjectBuilding(candidate, building) &&
      candidate.areas.some(
        (candidateArea) => objectAreaKey(candidate, candidateArea) === areaKey,
      ),
  );
}

function mergeObjectBuildingEntry(
  existing: GfsBuildingSnapshot,
  incoming: GfsBuildingSnapshot,
  area: GfsAreaSnapshot,
) {
  return mergeObjectBuildingSnapshots(existing, {
    ...incoming,
    areas: [area],
  });
}

function mergeObjectBuildingSnapshots(
  existing: GfsBuildingSnapshot,
  incoming: GfsBuildingSnapshot,
) {
  const levels = [...(existing.levels ?? [])];
  const levelIds = new Map<string, string>();

  for (const incomingLevel of incoming.levels ?? []) {
    const existingLevel = levels.find(
      (level) =>
        level.id === incomingLevel.id ||
        (level.type === incomingLevel.type &&
          normalizeObjectName(level.name) ===
            normalizeObjectName(incomingLevel.name)),
    );
    if (existingLevel) {
      levelIds.set(incomingLevel.id, existingLevel.id);
      continue;
    }
    levels.push(incomingLevel);
    levelIds.set(incomingLevel.id, incomingLevel.id);
  }

  const merged: GfsBuildingSnapshot = {
    ...existing,
    address: { ...incoming.address },
    addressKey: incoming.addressKey,
    levels,
    areas: [...(existing.areas ?? [])],
  };

  for (const incomingArea of incoming.areas ?? []) {
    const area = {
      ...incomingArea,
      levelId: incomingArea.levelId
        ? (levelIds.get(incomingArea.levelId) ?? incomingArea.levelId)
        : undefined,
    };
    if (
      !merged.areas.some(
        (existingArea) =>
          objectAreaKey(merged, existingArea) === objectAreaKey(merged, area),
      )
    ) {
      merged.areas.push(area);
    }
  }

  return merged;
}

function addressKey(address: Address) {
  return [
    address.company,
    address.department,
    address.contact,
    address.street,
    address.postbox,
    address.postalCode,
    address.city,
    address.country,
  ].join("\x1f");
}

function hasAddress(address: Address) {
  return oneLine(address) !== "";
}

function ObjectDocumentOverrides({
  document,
  disabled,
  onChange,
}: {
  document: GfsOfferDocumentSnapshot;
  disabled: boolean;
  onChange: (document: GfsOfferDocumentSnapshot) => void;
}) {
  const rows = [
    getObjectDocumentRow(document, "object", t("Objekt:"), 30),
    getObjectDocumentRow(document, "scope", t("Umfang:"), 60),
  ];

  const updateRow = (
    row: GfsOfferDocumentRow,
    patch: Partial<GfsOfferDocumentRow>,
  ) => {
    const hasRow = document.rows.some((candidate) => candidate.key === row.key);
    const nextRow = {
      ...row,
      ...patch,
    };
    onChange({
      ...document,
      rows: hasRow
        ? document.rows.map((candidate) =>
            candidate.key === row.key ? nextRow : candidate,
          )
        : [...document.rows, nextRow],
    });
  };

  return (
    <section className="space-y-5 border-b pb-6">
      {rows.map((row) => (
        <section className="space-y-4" key={row.key}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">{row.label}</h2>
            <div className="flex items-center gap-2">
              <Switch
                disabled={disabled}
                checked={row.automatic}
                onCheckedChange={(automatic) => updateRow(row, { automatic })}
              />
              <span className="text-sm">{t("Automatisch")}</span>
              {!row.automatic && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={disabled}
                  onClick={() => updateRow(row, { automatic: true })}
                >
                  {t("Automatik zurücksetzen")}
                </Button>
              )}
            </div>
          </div>
          <GfsOfferHtmlEditor
            disabled={disabled || row.automatic}
            value={row.value ?? ""}
            onChange={(value) => updateRow(row, { value })}
          />
        </section>
      ))}
    </section>
  );
}

function getObjectDocumentRow(
  document: GfsOfferDocumentSnapshot,
  key: "object" | "scope",
  label: string,
  order: number,
): GfsOfferDocumentRow {
  return (
    document.rows.find((row) => row.key === key) ?? {
      key,
      label,
      visible: true,
      order,
      blocks: [],
      automatic: true,
      value: "",
    }
  );
}
